<template>
  <div :class="(isHovering ? 'tw-bg-slate-500 ' : '') + 'tw-p-2 tw-flex tw-flex-col tw-relative'"
       @drop.prevent="onDrop"
       @dragenter.prevent="setHovering"
       @dragover.prevent="setHovering"
       @dragleave.prevent="unsetHovering">
    <img class="tw-object-scale-down tw-max-h-full tw-m-auto" v-if="currentImage" :src="imgUrl" />
    <img class="tw-object-scale-down tw-max-h-full tw-m-auto" v-else-if="hasImage" :src="`data:image/${props.initialImageType};base64,${props.initialImage}`" />

    <input type="file" ref="uploadButton" class="tw-hidden" @change.prevent="onFileSelected">

    <div class="tw-flex tw-flex-row tw-mt-5 tw-font-xl tw-absolute -tw-top-14 tw-right-4 tw-space-x-5">
      <div class="input-group">
        <button class="btn btn-primary btn-sm" @click="uploadButton?.click()">
          <font-awesome-icon :icon="['fas', 'upload']" />
          {{ i18n.t('action.upload') }}
        </button>
        <button class="btn btn-outline-danger btn-sm" v-if="hasImage" @click="removeImage">
          <font-awesome-icon :icon="['fas', 'trash']" />
          {{ i18n.t('action.delete') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, onMounted, onUnmounted, ref, watch} from 'vue'
import {useI18n} from "vue-i18n";

const props = defineProps({
  initialImage: { type: String, required: false },
  initialImageType: { type: String, required: false }
})

const emit = defineEmits<{
  (e: "image-changed", file: File | undefined): void
}>()

const i18n = useI18n();

const isHovering = ref(false);
const removedImage = ref(false);
const currentImage = ref<File | undefined>();

const uploadButton = ref<HTMLInputElement | null>(null);

const imgUrl = computed(() => {
  if (!currentImage.value) return undefined;

  return URL.createObjectURL(currentImage.value);
});

function setHovering() {
  isHovering.value = true;
}

function unsetHovering() {
  isHovering.value = false;
}

function onDrop(e: DragEvent) {
  if (!e.dataTransfer || !(e.dataTransfer?.files.length > 0)) return;
  unsetHovering();
  currentImage.value = e.dataTransfer.files[0];
  removedImage.value = false;
  emit('image-changed', currentImage.value)
}

function onFileSelected(e: Event) {
  const target = e.target as HTMLInputElement;
  if (!target || !(target.files?.length ?? 0 > 0)) return;
  currentImage.value = target.files![0];
  removedImage.value = false;
  emit('image-changed', currentImage.value)
}

function removeImage() {
  removedImage.value = true;
  currentImage.value = undefined;
  emit('image-changed', currentImage.value)
}

function preventDefaults(e: Event) {
  e.preventDefault()
}

const events = ['dragenter', 'dragover', 'dragleave', 'drop']

const hasImage = computed(() => {
  return !removedImage.value && (currentImage.value || props.initialImage && props.initialImageType);
})

onMounted(() => {
  events.forEach((eventName) => {
    document.body.addEventListener(eventName, preventDefaults)
  })
})

watch(() => props.initialImage, async () => {
  currentImage.value = undefined;
});

onUnmounted(() => {
  events.forEach((eventName) => {
    document.body.removeEventListener(eventName, preventDefaults)
  })
})
</script>
